/**
 * Diálogo "Finalizar ingreso": lo que quedó sin marcar pasa a NO RECIBIDA con un
 * motivo (obligatorio) y un detalle opcional. No se informa nada a ANMAT: las
 * transacciones quedan pendientes a nombre del emisor y el reclamo se hace por
 * fuera. El acta queda Confirmada, Parcial (hay no recibidas) o Con errores
 * (ANMAT rechazó alguna).
 */

import { useEffect, useState } from 'react';
import { Loader2, Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MOTIVOS_NO_RECIBIDA,
  MOTIVO_NO_RECIBIDA_LABEL,
  type MotivoNoRecibida,
} from '@/domain/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendientes: number;
  marcadas: number;
  errores: number;
  pending: boolean;
  onCerrar: (input: { motivo: MotivoNoRecibida | null; detalle: string | null }) => void;
}

export function CerrarActaDialog({
  open,
  onOpenChange,
  pendientes,
  marcadas,
  errores,
  pending,
  onCerrar,
}: Props) {
  const [motivo, setMotivo] = useState<MotivoNoRecibida | ''>('');
  const [detalle, setDetalle] = useState('');
  useEffect(() => {
    if (open) {
      setMotivo('');
      setDetalle('');
    }
  }, [open]);

  const bloqueado = marcadas > 0;
  const requiereMotivo = pendientes > 0;
  const puedeCerrar = !bloqueado && (!requiereMotivo || motivo !== '');

  return (
    <Dialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-everex-azul">Finalizar ingreso</DialogTitle>
          <DialogDescription className="text-xs">
            {pendientes > 0 ? (
              <>
                <b>{pendientes}</b> {pendientes === 1 ? 'serie queda' : 'series quedan'} sin marcar
                y {pendientes === 1 ? 'va a quedar' : 'van a quedar'} como <b>no recibida</b>
                {pendientes === 1 ? '' : 's'}. No se informa nada a ANMAT: siguen pendientes a
                nombre del emisor hasta que él las cancele.
              </>
            ) : (
              'No quedan series sin resolver. El ingreso se finaliza con lo confirmado.'
            )}
            {errores > 0 && (
              <>
                {' '}
                Hay <b>{errores}</b> {errores === 1 ? 'serie rechazada' : 'series rechazadas'} por
                ANMAT: el acta va a quedar <b>Con errores</b>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {bloqueado ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-900">
            Hay <b>{marcadas}</b> {marcadas === 1 ? 'serie marcada' : 'series marcadas'} sin
            confirmar. Confirmalas o desmarcalas antes de finalizar.
          </div>
        ) : requiereMotivo ? (
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs">Motivo de la no recepción</Label>
              <Select value={motivo} onValueChange={(v) => setMotivo(v as MotivoNoRecibida)}>
                <SelectTrigger>
                  <SelectValue placeholder="Elegí un motivo" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIVOS_NO_RECIBIDA.map((m) => (
                    <SelectItem key={m} value={m}>
                      {MOTIVO_NO_RECIBIDA_LABEL[m]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Detalle (opcional)</Label>
              <Input
                value={detalle}
                onChange={(e) => setDetalle(e.target.value)}
                maxLength={500}
                placeholder="Ej.: el bulto llegó abierto; faltan 2 unidades"
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancelar
          </Button>
          <Button
            onClick={() =>
              onCerrar({ motivo: motivo === '' ? null : motivo, detalle: detalle.trim() || null })
            }
            disabled={pending || !puedeCerrar}
            className="bg-everex-azul text-white hover:bg-everex-azul/90"
          >
            {pending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Lock className="mr-2 h-4 w-4" />
            )}
            Finalizar ingreso
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
